import type { QTableColumn } from 'quasar';
import { formatDate } from 'src/utils/formatDate';
import { getStartMethodLabel, StartMethod } from 'src/models/enum/StartMethod';
import { getRunStatusLabel, RunStatus } from './enum/RunStatus';
import { BehaviorAfterSave } from './enum/BeheviorAfterSave';
import {
  MailSettingsModel,
  GetMailSettings,
  SendMailSettings,
  SendMailSettingAnswerRequest,
  SendMailSettingStartNotification,
} from './MailSettingsModel';
import { QuestionCategoryContainQuestionTemplatesAttributes } from './QuestionCategoryModel';
export type GetCondition = {
  prefectures: { code: number; name: string }[];
  groups: { id: string; name: string }[];
  resources: { id: string; name: string }[];
};
export type SendCondition = {
  prefecture_codes: number[];
  group_ids: string[];
  resource_ids: string[];
};
export type EnqueteGetAttributes = {
  id: string;
  name: string;
  run_status: RunStatus;
  behavior_after_save: BehaviorAfterSave;
  start_method: StartMethod;
  resource_type: { id: string; name: string };
  enquete_template: { id: string; name: string } | null;
  event: { id: string; name: string } | null;
  incident: { id: string; name: string } | null;
  question_template_ids: string[];
  question_categories: QuestionCategoryContainQuestionTemplatesAttributes[];
  mail_settings: GetMailSettings;
  target_resources_conditions: GetCondition;
  created_at: string;
  updated_at: string;
  started_at: string;
  finished_at: string;
};
export type EnqueteSendAttributes = {
  name: string;
  behavior_after_save: BehaviorAfterSave;
  resource_type_id: string;
  enquete_template_id: string | null;
  event_id: string | null;
  incident_id: string | null;
  question_template_ids: string[];
  mail_settings: SendMailSettings;
  target_resources_conditions: SendCondition;
};
export type SearchType = 'prefecture' | 'group_resource' | 'all';
export class EnqueteModel {
  id: string;
  name: string;
  run_status: RunStatus;
  behavior_after_save: BehaviorAfterSave;
  start_method: StartMethod;
  resource_type: { id: string; name: string } | null;
  enquete_template: { id: string; name: string } | null;
  event: { id: string; name: string } | null;
  incident: { id: string; name: string } | null;
  question_template_ids: string[];
  question_categories: QuestionCategoryContainQuestionTemplatesAttributes[];
  mail_settings: MailSettingsModel;
  target_resources_conditions: GetCondition;
  started_at: string;
  finished_at: string;
  created_at: string;
  updated_at: string;
  search_type: SearchType;

  constructor(input?: EnqueteGetAttributes) {
    this.id = input ? input.id : '';
    this.name = input ? input.name : '';
    this.run_status = input ? input.run_status : 'draft';
    this.behavior_after_save = input ? input.behavior_after_save : 'draft';
    this.start_method = input ? input.start_method : 'manual';
    this.resource_type = input ? input.resource_type : null;
    this.enquete_template = input ? input.enquete_template : null;
    this.event = input ? input.event : null;
    this.incident = input ? input.incident : null;
    this.question_template_ids = input ? input.question_template_ids : [];
    this.question_categories = input ? input.question_categories : [];
    this.mail_settings = input
      ? new MailSettingsModel(input.mail_settings)
      : new MailSettingsModel();
    this.target_resources_conditions = input
      ? input.target_resources_conditions
      : {
          prefectures: [],
          groups: [],
          resources: [],
        };
    this.created_at = input ? formatDate(input.created_at) : '';
    this.updated_at = input ? formatDate(input.updated_at) : '';
    this.started_at = input ? formatDate(input.started_at) : '';
    this.finished_at = input ? formatDate(input.finished_at) : '';
    this.search_type = input
      ? input.target_resources_conditions.prefectures.length > 0
        ? 'prefecture'
        : input.target_resources_conditions.groups.length > 0 ||
          input.target_resources_conditions.resources.length > 0
        ? 'group_resource'
        : 'all'
      : 'prefecture';
  }

  getId(): string {
    return this.id;
  }
  getCreateAttributes(): EnqueteSendAttributes {
    return {
      name: this.name,
      behavior_after_save: this.behavior_after_save,
      resource_type_id: this.resource_type?.id as string,
      enquete_template_id: this.enquete_template
        ? this.enquete_template.id
        : null,
      event_id: this.event ? this.event.id : null,
      incident_id: this.incident ? this.incident.id : null,
      question_template_ids: this.question_template_ids,
      mail_settings: this.getMailSettings(),
      target_resources_conditions: this.getConditions(),
    };
  }
  getConditions(): SendCondition {
    return {
      prefecture_codes:
        this.search_type === 'prefecture'
          ? this.target_resources_conditions.prefectures.map(
              (prefecture) => prefecture.code
            )
          : [],
      group_ids:
        this.search_type === 'group_resource'
          ? this.target_resources_conditions.groups.map((group) => group.id)
          : [],
      resource_ids:
        this.search_type === 'group_resource'
          ? this.target_resources_conditions.resources.map(
              (resource) => resource.id
            )
          : [],
    };
  }
  getMailSettings(): SendMailSettings {
    return {
      answer_request: this.getMailSettingAnswerRequest(),
      start_notification: this.getMailSettingStartNotification(),
    };
  }
  getMailSettingAnswerRequest(): SendMailSettingAnswerRequest {
    if (this.mail_settings.answer_request.is_send) {
      return {
        is_send: this.mail_settings.answer_request.is_send,
        mail_template_id: this.mail_settings.answer_request.mail_template
          ? this.mail_settings.answer_request.mail_template.id
          : null,
        title: this.mail_settings.answer_request.title,
        body: this.mail_settings.answer_request.body,
        is_resend: this.mail_settings.answer_request.is_resend,
      };
    } else {
      return {
        is_send: this.mail_settings.answer_request.is_send,
      };
    }
  }
  getMailSettingStartNotification(): SendMailSettingStartNotification {
    if (this.mail_settings.start_notification.is_send) {
      return {
        is_send: this.mail_settings.start_notification.is_send,
        mail_template_id: this.mail_settings.start_notification.mail_template
          ? this.mail_settings.start_notification.mail_template.id
          : null,
        title: this.mail_settings.start_notification.title,
        body: this.mail_settings.start_notification.body,
      };
    } else {
      return {
        is_send: this.mail_settings.start_notification.is_send,
      };
    }
  }
}
export type EnqueteCollection = EnqueteModel[];

export const EnqueteTableColumns: QTableColumn[] = [
  {
    name: 'id',
    label: 'ID',
    align: 'left',
    field: 'id',
    sortable: true,
  },
  {
    name: 'name',
    label: 'アンケート名',
    align: 'left',
    field: 'name',
    sortable: true,
  },

  {
    name: 'resource_type',
    label: 'リソースタイプ',
    align: 'left',
    field: 'resource_type',
    sortable: true,
    format: (val) => val.name,
  },
  {
    name: 'run_status',
    label: '実施状況',
    align: 'left',
    field: 'run_status',
    sortable: true,
  },
  {
    name: 'start_method',
    label: '開始方法',
    align: 'left',
    field: 'start_method',
    sortable: true,
    format: getStartMethodLabel,
  },
  {
    name: 'created_at',
    label: '作成日',
    align: 'left',
    field: 'created_at',
    sortable: false,
  },
  {
    name: 'updated_at',
    label: '更新日',
    align: 'left',
    field: 'updated_at',
    sortable: false,
  },
];
